import { useCallback, useEffect, useState } from 'react'
import { api } from '../lib/api'
import type {
  GroupDetail,
  GroupResponse,
  GroupsResponse,
  GroupSummary,
  GroupSummaryResponse,
  SteamFriend,
  SteamFriendsResponse,
} from '../lib/types'

export function useGroups() {
  const [groups, setGroups] = useState<GroupSummary[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(
    () =>
      api
        .get<GroupsResponse>('/groups')
        .then((data) => {
          setGroups(data.groups)
          setError(null)
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : 'Could not load your groups.')
        })
        .finally(() => {
          setIsLoading(false)
        }),
    [],
  )

  useEffect(() => {
    refresh()
  }, [refresh])

  const createGroup = useCallback(async (name: string) => {
    const data = await api.post<GroupSummaryResponse>('/groups', { name })
    setGroups((prev) => [data.group, ...prev])
    return data.group
  }, [])

  return { groups, isLoading, error, refresh, createGroup }
}

export function useGroup(groupId: string | undefined) {
  const [group, setGroup] = useState<GroupDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(() => {
    if (!groupId) return Promise.resolve()
    return api
      .get<GroupResponse>(`/groups/${groupId}`)
      .then((data) => {
        setGroup(data.group)
        setError(null)
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Could not load this group.')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [groupId])

  useEffect(() => {
    refresh()
  }, [refresh])

  const addMember = useCallback(
    async (steamId: string) => {
      if (!groupId) return
      await api.post(`/groups/${groupId}/members`, { steam_id: steamId })
      await refresh()
    },
    [groupId, refresh],
  )

  return { group, isLoading, error, refresh, addMember }
}

/**
 * Lazily loads the signed-in user's Steam friends (GET /me/steam_friends),
 * ORDERED by who they actually play with, for the "add from your Steam friends"
 * picker. Only fetches when `enabled` flips true (the dialog opens), so we don't
 * hit the Steam Web API on every render. Passing `groupId` lets the server flag
 * friends already in that group (`already_member`). A private friend list comes
 * back typed as { status: 'private' } — surfaced, not an error — so the picker
 * can show a friendly note while the Steam-ID field still works.
 */
export function useSteamFriends(enabled: boolean, groupId?: string) {
  const [friends, setFriends] = useState<SteamFriend[]>([])
  const [status, setStatus] = useState<'ok' | 'private' | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Loading flips on while rendering (guarded by the previous value, per the
  // React docs) rather than in the effect: each newly-enabled fetch target
  // marks itself loading before its request fires.
  const path = groupId ? `/me/steam_friends?group_id=${groupId}` : '/me/steam_friends'
  const fetchKey = enabled ? path : null
  const [prevFetchKey, setPrevFetchKey] = useState<string | null>(null)
  if (fetchKey !== prevFetchKey) {
    setPrevFetchKey(fetchKey)
    if (fetchKey !== null) setIsLoading(true)
  }

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    api
      .get<SteamFriendsResponse>(path)
      .then((data) => {
        if (cancelled) return
        setFriends(data.friends)
        setStatus(data.status)
        setError(null)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : 'Could not load your Steam friends.')
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [enabled, path])

  return { friends, status, isLoading, error }
}
